import { useAppStore } from '@/stores/useAppStore';
import { Device, ILogEntry, Settings, Store } from 'app-core';
import { CapacitorUtils } from 'capacitor-utils';
import { nextTick } from 'vue';

type ConsoleMethod = 'log' | 'debug' | 'warn' | 'info' | 'error';

let store: ReturnType<typeof useAppStore> | null = null;
let isLogging = false;
let logQueue: ILogEntry[] = []
let isInitialized = false;

// Store references for cleanup
let electronLogHandler: ((event: any, eventData: ILogEntry) => void) | null = null;
let capacitorLogHandler: ((eventData: ILogEntry) => void) | null = null;
let originalConsoleMethods: Partial<Record<ConsoleMethod, Function>> = {};

const exclude = [
  '[intlify]',
  '[Vue warn]',
  '[vite]',
]

async function initializeLogging() {
  if (isInitialized) return;

  store = useAppStore();
  const debugMode = Store.get<boolean>('debugMode')

  if(Device.isElectron()) {
    electronLogHandler = (event: any, eventData: ILogEntry) => storeLog(eventData);
    Settings.electron?.on('nativeLog', electronLogHandler);
    Settings.electron?.invoke('logSetDebugMode', debugMode);
  } else if(Device.isMobile()) {
    capacitorLogHandler = (eventData: ILogEntry) => storeLog(eventData);
    CapacitorUtils.addListener('nativeLog', capacitorLogHandler);
    CapacitorUtils.logSetDebugMode({ enabled: debugMode });
  }

  overrideConsoleLog();
  isInitialized = true;
}

async function logSetDebugMode(debugMode: boolean) {
  Store.set('debugMode', debugMode)

  if(Device.isElectron()) {
    Settings.electron?.invoke('logSetDebugMode', debugMode);
  } else if(Device.isMobile()) {
    CapacitorUtils.logSetDebugMode({ enabled: debugMode });
  }
}

function storeLog(logEntry: ILogEntry) {
  if (exclude.some(excludeString => logEntry.message.includes(excludeString))) return

  logQueue.push(logEntry)

  // Prevent recursive calls during Vue render cycles
  if (isLogging) return

  isLogging = true;

  // Use nextTick to defer the store update until after the current render cycle
  nextTick(() => {
    try {
      if (!store) return;

      const logs = [...store.log, ...logQueue]

      while (logs.length > 2000) {
        logs.shift();
      }

      Store.set('log', logs);

      logQueue = []
    } catch (error) {
      console.error('Failed to add log:', error);
    } finally {
      isLogging = false;
    }
  });
}

function logToNative(logEntry: ILogEntry) {
  if (exclude.some(excludeString => logEntry.message.includes(excludeString))) return

  if (Device.isElectron()) {
    Settings.electron?.invoke('logToNative', logEntry);
  } else if (Device.isMobile()) {
    CapacitorUtils.logToNative(logEntry);
  }
}

function overrideConsoleLog(): void {
  if (Object.keys(originalConsoleMethods).length > 0) return; // Already overridden

  // Override each console method
  const levels: ConsoleMethod[] = ['log', 'debug', 'warn', 'info', 'error'];

  levels.forEach((level) => {
    originalConsoleMethods[level] = console[level]; // Store original

    console[level] = function(...args: any[]): void {
      const debugMode = Store.get<boolean>('debugMode');

      // If debug mode is off and this is a debug message, ignore completely
      if (!debugMode && level === 'debug') {
        return;
      }

      // Call original method (shows in browser console)
      originalConsoleMethods[level]!.apply(console, args);

      // Store the log
      const logEntry: ILogEntry = {
        message: args.map(arg => {
          try {
            if (typeof arg === 'object') {
              return safeStringify(arg);
            }
            return String(arg);
          } catch (err: any) {
            return `[Object: Stringify failed - ${err.message}]`;
          }
        }).join(' ').replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, ''),
        timestamp: new Date().toISOString(),
        level: level === 'log' ? 'info' : level,
        scope: 'CONSOL'
      };

      storeLog(logEntry);
      logToNative(logEntry);
    };
  });
}

function loggingCleanup() {
  try {
    if (Device.isElectron() && electronLogHandler) {
      Settings.electron?.off('nativeLog', electronLogHandler);
      electronLogHandler = null;
    } else if (Device.isMobile()) {
      CapacitorUtils.removeAllListeners();
      capacitorLogHandler = null;
    }
  } catch (error) {
    console.warn('Logging cleanup error', error);
  }

  // Restore console methods
  Object.keys(originalConsoleMethods).forEach(level => {
    try {
      console[level as ConsoleMethod] = originalConsoleMethods[level as ConsoleMethod] as any;
    } catch (error) {
      console.warn(`Logging cleanup: Failed to restore console.${level}:`, error);
    }
  });

  originalConsoleMethods = {};

  // Reset state
  isInitialized = false;
  store = null;
  isLogging = false;
  logQueue = []
}

/**
 * Safely stringifies an object, handling circular references.
 * @param obj The object to stringify.
 * @param space An optional indentation for pretty printing.
 * @returns A string representation of the object.
 */
function safeStringify(obj: any, space?: number | string): string {
  const cache = new WeakSet();
  return JSON.stringify(
    obj,
    (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (cache.has(value)) {
          // Circular reference found, discard key
          return '[Circular]'; // Or undefined to omit, or a custom representation
        }
        // Store value in our collection
        cache.add(value);
      }
      // BigInts are not serializable by default
      if (typeof value === 'bigint') {
        return value.toString() + 'n';
      }
      return value;
    },
    space
  );
}

export function useLog() {
  return {
    initializeLogging,
    loggingCleanup,
    logSetDebugMode,
  }
}
